/**
 * LaboratoriesPage (Phase-2, Module 7 — admin side)
 *
 * Register laboratories, approve / activate / reject registrations, and manage
 * each lab's test catalog (add / toggle / delete tests).
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, IconButton, Tooltip, Chip, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, FormControl, InputLabel, Select, Typography, Divider,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Science as LabIcon, Add as AddIcon, ListAlt as CatalogIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, ConfirmDialog } from '../../components/common';
import { LaboratoryApi } from '../../services/phase2Api';
import { LAB_TYPES, labelFor } from '../../utils/phase2Constants';
import type { Laboratory, LabTestCatalog, LabTestCatalogRequest } from '../../types/phase2';

const LaboratoriesPage: React.FC = () => {
  const [rows, setRows] = useState<Laboratory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regOpen, setRegOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rejectId, setRejectId] = useState<number | null>(null);

  // catalog dialog
  const [catLab, setCatLab] = useState<Laboratory | null>(null);
  const [catalog, setCatalog] = useState<LabTestCatalog[]>([]);
  const [testForm, setTestForm] = useState<LabTestCatalogRequest>({ testName: '', testCode: '', testCategory: '', price: 0, sampleType: '', turnaroundTimeHours: undefined, homeCollectionPrice: undefined });

  const [regForm, setRegForm] = useState({
    labName: '', labType: 'PRIVATE', city: '', state: '', phone: '', email: '',
    contactPerson: '', contactPersonMobile: '', licenseNumber: '', addressLine1: '',
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const page = await LaboratoryApi.list({ page: 0, size: 100 });
      setRows(page?.content || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load laboratories'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRegister = async () => {
    if (!regForm.labName || !regForm.labType) return;
    setSaving(true); setError(null);
    try {
      await LaboratoryApi.register({
        labName: regForm.labName, labType: regForm.labType, city: regForm.city || undefined,
        state: regForm.state || undefined, phone: regForm.phone || undefined, email: regForm.email || undefined,
        contactPerson: regForm.contactPerson || undefined, contactPersonMobile: regForm.contactPersonMobile || undefined,
        licenseNumber: regForm.licenseNumber || undefined, addressLine1: regForm.addressLine1 || undefined,
      });
      setRegOpen(false);
      setRegForm({ labName: '', labType: 'PRIVATE', city: '', state: '', phone: '', email: '', contactPerson: '', contactPersonMobile: '', licenseNumber: '', addressLine1: '' });
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Registration failed'); }
    finally { setSaving(false); }
  };

  const openCatalog = async (lab: Laboratory) => {
    setCatLab(lab);
    try { setCatalog((await LaboratoryApi.listCatalog(lab.id)) || []); }
    catch { setCatalog([]); }
  };

  const addTest = async () => {
    if (!catLab || !testForm.testName || !testForm.price) return;
    try {
      await LaboratoryApi.addCatalogTest(catLab.id, {
        testName: testForm.testName, testCode: testForm.testCode || undefined,
        testCategory: testForm.testCategory || undefined, price: Number(testForm.price),
        sampleType: testForm.sampleType || undefined,
        turnaroundTimeHours: testForm.turnaroundTimeHours ? Number(testForm.turnaroundTimeHours) : undefined,
        homeCollectionPrice: testForm.homeCollectionPrice ? Number(testForm.homeCollectionPrice) : undefined,
      });
      setCatalog((await LaboratoryApi.listCatalog(catLab.id)) || []);
      setTestForm({ testName: '', testCode: '', testCategory: '', price: 0, sampleType: '', turnaroundTimeHours: undefined, homeCollectionPrice: undefined });
    } catch (e: any) { setError(e?.response?.data?.message || 'Add test failed'); }
  };

  const handleReject = async (reason?: string) => {
    if (rejectId == null || !reason) return;
    try { await LaboratoryApi.reject(rejectId, reason); setRejectId(null); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Reject failed'); }
  };

  const act = async (fn: () => Promise<Laboratory>, label: string) => {
    try { await fn(); load(); } catch (e: any) { setError(e?.response?.data?.message || `${label} failed`); }
  };

  const columns: GridColDef[] = [
    { field: 'labName', headerName: 'Laboratory', flex: 1.4, minWidth: 180 },
    { field: 'labType', headerName: 'Type', width: 130, renderCell: (p) => <Chip size="small" label={labelFor(LAB_TYPES, p.value)} variant="outlined" /> },
    { field: 'city', headerName: 'City', width: 130, renderCell: (p) => p.value || '—' },
    { field: 'phone', headerName: 'Phone', width: 140, renderCell: (p) => p.value || '—' },
    { field: 'status', headerName: 'Status', width: 140, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 300, sortable: false,
      renderCell: (p) => {
        const s = p.row.status as string;
        const id = p.row.id as number;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {s === 'PENDING' && <Button size="small" variant="contained" color="success" onClick={() => act(() => LaboratoryApi.approve(id), 'Approve')} sx={{ textTransform: 'none', mr: 0.5 }}>Approve</Button>}
            {(s === 'APPROVED' || s === 'INACTIVE') && <Button size="small" variant="contained" color="primary" onClick={() => act(() => LaboratoryApi.activate(id), 'Activate')} sx={{ textTransform: 'none', mr: 0.5 }}>Activate</Button>}
            {s === 'PENDING' && <Button size="small" variant="outlined" color="error" onClick={() => setRejectId(id)} sx={{ textTransform: 'none', mr: 0.5 }}>Reject</Button>}
            <Tooltip title="Test Catalog"><IconButton size="small" onClick={() => openCatalog(p.row)}><CatalogIcon fontSize="small" /></IconButton></Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <PageScaffold title="Laboratories" subtitle="Register & approve diagnostic laboratories" icon={<LabIcon />}
      actionLabel="Register Lab" actionIcon={<AddIcon />} onAction={() => setRegOpen(true)}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      <Box sx={{ height: 560, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>

      {/* Register dialog */}
      <Dialog open={regOpen} onClose={() => !saving && setRegOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Register Laboratory</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Laboratory Name" required value={regForm.labName} onChange={(e) => setRegForm({ ...regForm, labName: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={regForm.labType} onChange={(e) => setRegForm({ ...regForm, labType: e.target.value })}>
                {LAB_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="License Number" value={regForm.licenseNumber} onChange={(e) => setRegForm({ ...regForm, licenseNumber: e.target.value })} fullWidth />
            <TextField label="Address" value={regForm.addressLine1} onChange={(e) => setRegForm({ ...regForm, addressLine1: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="City" value={regForm.city} onChange={(e) => setRegForm({ ...regForm, city: e.target.value })} fullWidth />
              <TextField label="State" value={regForm.state} onChange={(e) => setRegForm({ ...regForm, state: e.target.value })} fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Phone" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} fullWidth />
              <TextField label="Email" value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Contact Person" value={regForm.contactPerson} onChange={(e) => setRegForm({ ...regForm, contactPerson: e.target.value })} fullWidth />
              <TextField label="Contact Mobile" value={regForm.contactPersonMobile} onChange={(e) => setRegForm({ ...regForm, contactPersonMobile: e.target.value })} fullWidth />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRegOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleRegister} disabled={!regForm.labName || saving} sx={{ textTransform: 'none', fontWeight: 600 }}>{saving ? 'Saving...' : 'Register'}</Button>
        </DialogActions>
      </Dialog>

      {/* Catalog dialog */}
      <Dialog open={!!catLab} onClose={() => setCatLab(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>Test Catalog — {catLab?.labName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="subtitle2">Add Test</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField size="small" label="Test Name" value={testForm.testName} onChange={(e) => setTestForm({ ...testForm, testName: e.target.value })} sx={{ minWidth: 180 }} />
              <TextField size="small" label="Code" value={testForm.testCode} onChange={(e) => setTestForm({ ...testForm, testCode: e.target.value })} sx={{ width: 110 }} />
              <TextField size="small" label="Category" value={testForm.testCategory} onChange={(e) => setTestForm({ ...testForm, testCategory: e.target.value })} sx={{ width: 140 }} />
              <TextField size="small" label="Sample" value={testForm.sampleType} onChange={(e) => setTestForm({ ...testForm, sampleType: e.target.value })} sx={{ width: 130 }} />
              <TextField size="small" type="number" label="Price ₹" value={testForm.price} onChange={(e) => setTestForm({ ...testForm, price: Number(e.target.value) })} sx={{ width: 110 }} />
              <TextField size="small" type="number" label="Home ₹" value={testForm.homeCollectionPrice ?? ''} onChange={(e) => setTestForm({ ...testForm, homeCollectionPrice: e.target.value ? Number(e.target.value) : undefined })} sx={{ width: 110 }} />
              <Button variant="contained" onClick={addTest} disabled={!testForm.testName || !testForm.price} sx={{ textTransform: 'none' }}>Add</Button>
            </Box>
            <Divider />
            <Stack spacing={1}>
              {catalog.map((t) => (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px dashed #E5E7EB', py: 0.5 }}>
                  <Box>
                    <Typography variant="body2">{t.testName} <Chip size="small" label={t.testCategory || '—'} sx={{ ml: 1 }} /> {t.isActive === false && <Chip size="small" color="default" label="inactive" />}</Typography>
                    <Typography variant="caption" color="text.secondary">₹{t.price} {t.homeCollectionPrice ? `· home ₹${t.homeCollectionPrice}` : ''}</Typography>
                  </Box>
                  <Box>
                    <Button size="small" onClick={async () => { await LaboratoryApi.toggleCatalogTest(t.id, t.isActive !== false); if (catLab) setCatalog((await LaboratoryApi.listCatalog(catLab.id)) || []); }} sx={{ textTransform: 'none' }}>{t.isActive === false ? 'Enable' : 'Disable'}</Button>
                    <Button size="small" color="error" onClick={async () => { await LaboratoryApi.deleteCatalogTest(t.id); if (catLab) setCatalog((await LaboratoryApi.listCatalog(catLab.id)) || []); }} sx={{ textTransform: 'none' }}>Delete</Button>
                  </Box>
                </Box>
              ))}
              {catalog.length === 0 && <Typography variant="body2" color="text.secondary">No tests in catalog yet.</Typography>}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setCatLab(null)}>Close</Button></DialogActions>
      </Dialog>

      <ConfirmDialog open={rejectId != null} title="Reject Laboratory" message="Provide a reason for rejecting this laboratory registration."
        confirmLabel="Reject" requireReason reasonLabel="Rejection reason" onConfirm={handleReject} onClose={() => setRejectId(null)} />
    </PageScaffold>
  );
};

export default LaboratoriesPage;